import React, { useEffect, useRef, useState } from "react"
import _ from "lodash"
import Isvg from "react-inlinesvg"
// PR
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import {ContextMenu} from "primereact/contextmenu"
// Components
import CarouselAutomation from "components/carousel-automation"
import { field as _field } from "components/table/lib/column-field"
import SearchHeader from "components/table/search-header"
import { Button_s } from "styles/app-styles"
import {
  TableAutomation,
  focusFirstCellByRowIndex,
  focusCellByContent,
  focusCellByDataId,
  pageToFirst,
  getPagingInfo,
  getFocusedRowIndex
} from "components/table/table-automation"

import Controls from "components/area-measures/controls/"
import { CheckboxInput, DropdownEdit } from "components/table/inputs"
import { InputText_s } from "styles/input-styles"
import { DropdownField } from "components/table/display"
import { DuplicateModal } from "components/modal/duplicate-confirmation"
// Data
import automationService from "data/automation"
import assetListService from "data/asset-list"
import { statesAndProvinces } from "data/lib/statesAndProvinces"
import { handleReferences } from "data/lib/handle-references"
import validationService, { IValidationErrors } from "data/validate"
import { copy, paste, canPaste } from "data/lib/copy-paste"
// Styles
import { AssetListTable_s } from "components/asset-management/asset-list/asset-list-styles"
// Icons
import { CommercialIcon, HospitalityIcon, MFRentIcon } from 'images/icons/sidebar'

type ContextRow = {
  assetid: number
  code: string
  sortorder: number
  systeminput?: string
}

const AssetListTable = (props: {tableId: string, data: object[]}) => {
  const selected = [] // place holder during multiselect refactor
  const usePrevious = (value) =>{
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const nonSystemMenu = [
    {label: 'Duplicate Row', icon: 'pi pi-fw pi-clone', command: e => { if(contextRow.systeminput) return;
      assetListService.copy(contextRow.assetid, contextRow.code)
    }},
    {label: 'Duplicate Row n Times', icon: 'pi pi-fw pi-clone', command: async e => { if(contextRow.systeminput) return;
      showDuplicateConfirmation(async (n) => {
        await assetListService.copies(contextRow.assetid, n)
      })
    }},
    {label: 'Delete Row', icon: 'pi pi-fw pi-trash', command: e => { if(contextRow.systeminput) return;
      const pager         = getPagingInfo(props.tableId);
      const currentRowNum = getFocusedRowIndex() + 1
      const currentRecordIndex = (pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1
      showDeleteConfirmation(contextRow, currentRecordIndex + 1)
    }},
    {label: 'Trace', icon: 'pi pi-fw pi-search', command: e=> {
      const refreshFn = () => assetListService.fetchAssets()
      handleReferences('asset', contextRow.assetid, {refreshFn});
  }},
  ]

  const [filteredData, setFilteredData] = useState<object[]>(props.data);
  const [searchFilter, setSearchFilter] = useState<string>("")
  const [contextRow, setContextRow] = useState<ContextRow>({assetid: 0, code: '', sortorder: 0})
  const [contextCell, setContextCell] = useState<object|null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<JSX.Element | null>(null)
  const [duplicateConfirmation, setDuplicateConfirmation] = useState<JSX.Element | null>(null)
  const menuRef = useRef<ContextMenu | null>(null)
  const [menu, setMenu] = useState<[]|object[]>(nonSystemMenu)
  const prevSearch = usePrevious(searchFilter);
  const [automationActive, setAutomationActive] = useState<boolean>(false);
  const [tableAutomationActive, setTableAutomationActive] = useState<boolean>(true);
  const [selectedPropertyType, setSelectedPropertyType] = useState<string>("All")
  const [activeFilter, setActiveFilter] = useState<string>("Active")
  const [codeFilter, setCodeFilter] = useState<string>("");
  const [nameFilter, setNameFilter] = useState<string>("");
  const [countryFilter, setCountryFilter] = useState<string>("All");
  const [cityFilter, setCityFilter] = useState<string>("");
  const [stateFilter, setStateFilter] = useState<string>("");
  const [addressFilter, setAddressFilter] = useState<string>("");
  const [postalCodeFilter, setPostalCodeFilter] = useState<string>("");
  const tableRef = useRef() as React.MutableRefObject<DataTable>
  const [_errors, setErrors] = useState<IValidationErrors>({})
  const field = _field('asset')

  useEffect(() => {
    const assetListEventSubscription = assetListService.eventStream().subscribe(e => {
      switch(true) {
        case e.type === "ASSET_LIST_CREATED":
          setTimeout(() => pageToFirst(props.tableId), 1)
          setTimeout(() => {
            focusFirstCellByRowIndex(props.tableId, 0)
          }, 500)
          break
        case e.type === "ASSET_LIST_COPIED":
          setTimeout(() => {
            focusCellByContent(props.tableId, e.payload.code)
          }, 500)
      }
    })

    const validationSubscription = validationService.stream().subscribe((e) => {
      setErrors(e)
    })

    return function cleanup() {
      assetListEventSubscription.unsubscribe()
      validationSubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (prevSearch !== "" && searchFilter === "") _.invoke(tableRef, 'current.filter', '', 'contains');
    else if (searchFilter !== "") _.invoke(tableRef, 'current.filter', searchFilter, 'contains');
    else return
  }, [searchFilter])

  useEffect(() => {
    filterData();
  }, [props.data, selectedPropertyType, codeFilter, nameFilter, activeFilter, addressFilter, cityFilter, stateFilter, countryFilter, postalCodeFilter])

  useEffect(() => {
    setMenu(nonSystemMenu)
  }, [contextRow])

  function filterData():void {
    let nextData = _.cloneDeep(props.data);

    if (selectedPropertyType !== 'All') nextData = nextData.filter(item => _.get(item, 'propertytype') === selectedPropertyType)
    if (countryFilter !== 'All') nextData = nextData.filter(item => _.get(item, 'country') === countryFilter)

    if (activeFilter === 'Active'){
      nextData = nextData.filter(item => _.get(item, 'active') === true)
    } else if (activeFilter !== 'All') {
      nextData = nextData.filter(item => _.get(item, 'active') === false)
    }

    if (!_.isNil(codeFilter) && codeFilter !== "") nextData = nextData.filter(item => _.get(item, 'code')?.toLowerCase().includes(codeFilter.toLowerCase()))
    if (!_.isNil(nameFilter) && nameFilter !== "") nextData = nextData.filter(item => _.get(item, 'name')?.toLowerCase().includes(nameFilter.toLowerCase()))
    if (!_.isNil(addressFilter) && addressFilter !== "") nextData = nextData.filter(item => _.get(item, 'address')?.toLowerCase().includes(addressFilter.toLowerCase()))
    if (!_.isNil(cityFilter) && cityFilter !== "") nextData = nextData.filter(item => _.get(item, 'city')?.toLowerCase().includes(cityFilter.toLowerCase()))
    if (!_.isNil(stateFilter) && stateFilter !== "") nextData = nextData.filter(item => _.get(item, 'state') && _.find(statesAndProvinces, { value: _.get(item, 'state')})['label'].toLowerCase().includes(stateFilter.toLowerCase()))
    if (!_.isNil(postalCodeFilter) && postalCodeFilter !== "") nextData = nextData.filter(item => _.get(item, 'postalcode')?.toLowerCase().includes(postalCodeFilter.toLowerCase()))

    setFilteredData(nextData)
  }

  const showDeleteConfirmation = (record: ContextRow, currentRow: number) => {
    automationService.broadcast("MODAL_OPENING", {})
    setDeleteConfirmation(Controls.DeleteConfirmation({
      show: true,
      message: `Would you like to delete the ${selected.length} selected rows? Note this cannot be undone.`,
      close: () => {
        setDeleteConfirmation(null)
        setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
      },
      confirm: () => {
        if (selected.length > 1 ) {
          assetListService.deleteOne(_.map(selected, 'assetid'))
        } else {
          assetListService.deleteOne(record.assetid)
        }
        setDeleteConfirmation(null)
        setTimeout(() => {
          focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0)
        }, 200)
      },
      rowNum: currentRow,
      code: record.code,
      selected
    }))
  }

  const showDuplicateConfirmation = (copy: (n: number) => Promise<void>) => {
    setDuplicateConfirmation(
      <DuplicateModal
        close={() => {setDuplicateConfirmation(null); focusCellByDataId( props.tableId, contextRow.assetid)}}
        copy={copy}
        show={true}
        title={contextRow.code}
      />
    )
  }

  function updateContextMenu(originalEvent){
    setMenu(nonSystemMenu)
    _.invoke(menuRef.current, "show", originalEvent)
  }

  const rowClassName = (rowData) => {
    return {'' : rowData}
  }

  return (
    <div>
      <SearchHeader setSearchFilter={setSearchFilter} />
      <ContextMenu model={menu} ref={menuRef} appendTo={document.querySelector("body")} onHide={() => {}}/>
      <AssetListTable_s id={`${props.tableId}-scrollable-container`}>
        <DataTable
          globalFilter={searchFilter}
          id={props.tableId}
          ref={tableRef}
          value={filteredData}
          paginator={true}
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
          scrollable={true}
          frozenWidth="420px"
          paginatorPosition="bottom"
          rowClassName={rowClassName}
          onContextMenu={e => menuRef.current ? updateContextMenu(e.originalEvent) : null}
          contextMenuSelection={contextRow}
          onContextMenuSelectionChange={e => {
            setContextRow(e.value)
            setContextCell(e.originalEvent.target)
          }}
          stateStorage="custom"
          customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
          customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)}
        >
          <Column header="#" frozen={true} style={{minWidth: "45px"}}
            body={(rowData, props) => props.rowIndex + 1}
          />
          <Column frozen={true} style={{minWidth: "125px"}} className="p-editable-column property-type-column"
            filter
            sortable
            header="Property Type"
            body={(rowData, props) => <Isvg src={rowData.propertytype === 'Multifamily' ? MFRentIcon : rowData.propertytype === 'Hospitality' ? HospitalityIcon : CommercialIcon} />}
            filterElement={
              <DropdownEdit
                field="selectedPropertyType"
                rowData={{selectedPropertyType}}
                options={["All", "Commercial", "Multifamily", "Hospitality"]}
                persist={(val) => setSelectedPropertyType(val)}
              />
            }
          />
          <Column frozen={true} style={{minWidth: "250px"}} className="p-editable-column"
            filter
            sortable
            header="Code"
            {...field('code')}
            filterElement={
              <InputText_s value={codeFilter} onChange={(e) => setCodeFilter(e.target.value)} />
            }
          />
          <Column style={{minWidth: "250px", maxWidth: "250px"}} className="p-editable-column asset-name"
            filter
            sortable
            header="Name"
            {...field('name')}
            filterElement={
              <InputText_s value={nameFilter} onChange={(e) => setNameFilter(e.target.value)} />
            }
          />
          <Column style={{minWidth: "100px"}} className="p-editable-column"
            filter
            sortable
            header="Active"
            {...field('active', (rowData, props) => CheckboxInput({...props}))}
            filterElement={
              <DropdownEdit
                field="activeFilter"
                rowData={{activeFilter}}
                options={["All", "Active", "Inactive"]}
                persist={(val) => setActiveFilter(val)}
              />
            }
          />
          <Column style={{minWidth: "100px"}} className="p-editable-column"
            header="Details"
            body={(rowData, props) => <Button_s className="detailed-route-button" style={{height: '26px'}} onClick={()=>window.location.replace(`/n/asset-management/asset-list/${rowData.assetid}`)}>View/Edit</Button_s>}
          />
          <Column style={{minWidth: "100px"}} className="p-editable-column"
            filter
            sortable
            header="Country"
            {...field('country', (rowData, props) => DropdownField({options: [{value: 'US', label: 'US'},{value: 'CA', label: 'Canada'}], rowData, ...props}))}
            filterElement={
              <DropdownEdit
                field="countryFilter"
                rowData={{countryFilter}}
                options={["All", "US", "CA"]}
                persist={(val) => setCountryFilter(val)}
              />
            }
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column"
            filter
            sortable
            header="Address"
            {...field('address')}
            filterElement={
              <InputText_s value={addressFilter} onChange={(e) => setAddressFilter(e.target.value)} />
            }
          />
          <Column style={{minWidth: "180px"}} className="p-editable-column"
            filter
            sortable
            header="City"
            {...field('city')}
            filterElement={
              <InputText_s value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} />
            }
          />
          <Column style={{minWidth: "225px"}} className="p-editable-column"
            filter
            sortable
            header="State"
            {...field('state', (rowData, props) => DropdownField({options: statesAndProvinces, rowData, ...props}))}
            filterElement={
              <InputText_s value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} />
            }
          />
          <Column style={{minWidth: "130px"}} className="p-editable-column"
            filter
            sortable
            header="Postal Code"
            {...field('postalcode')}
            filterElement={
              <InputText_s value={postalCodeFilter} onChange={(e) => setPostalCodeFilter(e.target.value)} />
            }
          />
        </DataTable>
        { deleteConfirmation }
        { duplicateConfirmation }
        { props.data.length
          ? <div>
              <CarouselAutomation outsideTable={true} automationActive={automationActive} editableFilters={true} includeSearchBar={true} tabOut={() => { setAutomationActive(false); setTableAutomationActive(true) }}/>
              <TableAutomation tableId={props.tableId} automationActive={tableAutomationActive} shiftTabOut={() => setTableAutomationActive(false)}/>
            </div>
          : null
        }
      </AssetListTable_s>
    </div>
  )
}

export default AssetListTable
